import { DeckDTO } from '@/generated';
import { Delete, MoreVert, Visibility } from '@mui/icons-material';
import { Grid, IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Stack, Paper, Typography, Tooltip } from '@mui/material';
import { isDeckEditor } from './deck-utils';
import { useNavigate } from 'react-router-dom';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import EditIcon from '@mui/icons-material/Edit';
import { AppContext } from '@/contexts/app.context';
import { useContext, useState } from 'react';
import { pathMap } from '@/navigation/paths';
import { DeckImage } from './DeckImage';

interface CardExerciseProps {
  timeout: number;
  deck: DeckDTO;
  isSetCreator: boolean;
  isEditor: boolean;
  onClick?: () => void;
  onExerciseDeleted?: () => void;
}

const listPadding = 1;
const listGap = 0.5;

export const DeckListElement = (props: CardExerciseProps & { setShowDeleteModal: React.Dispatch<React.SetStateAction<boolean>> }) => {
  const { deck, isSetCreator } = props;
  const context = useContext(AppContext);
  const loggedInUser = context.state?.userProfile;
  const navigate = useNavigate();
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const canDelete = Boolean(loggedInUser && isDeckEditor(deck, loggedInUser));

  const openDeck = () => navigate(`${pathMap.Exercises}/${deck.id}`);

  return (
    <Grid
      onClick={(e) => {
        if (props.onClick) props.onClick();
        e.stopPropagation();
      }}
      sx={{ cursor: 'pointer' }}
      size={{ xs: 12, md: 12 }}
    >
      <Paper onClick={openDeck} sx={{ display: { xs: 'block', sm: 'none' }, p: 1.5 }} elevation={3}>
        <Stack direction="row" spacing={1.5} alignItems="flex-start">
          <DeckImage deck={deck} width={64} />
          <Stack spacing={0.5} sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle1" fontWeight={600} noWrap>
              {deck.name}
            </Typography>
            <Typography variant="body2" color="text.secondary" noWrap>
              Created by {!isSetCreator ? deck.creatorName : 'You'}
            </Typography>
            <DeckAccessIcons {...props} />
          </Stack>
          {canDelete && (
            <IconButton
              aria-label={`Actions for ${deck.name}`}
              aria-controls={menuAnchor ? `deck-actions-${deck.id}` : undefined}
              aria-haspopup="true"
              aria-expanded={menuAnchor ? 'true' : undefined}
              onClick={(event) => {
                event.stopPropagation();
                setMenuAnchor(event.currentTarget);
              }}
            >
              <MoreVert />
            </IconButton>
          )}
        </Stack>
        <Typography sx={{ display: 'block', mt: 1 }} variant="caption" color="text.secondary">
          Last updated {deck.modificationTimestamp || 'unknown'}
        </Typography>
        <Menu
          id={`deck-actions-${deck.id}`}
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={() => setMenuAnchor(null)}
          onClick={(event) => event.stopPropagation()}
        >
          <MenuItem
            onClick={() => {
              setMenuAnchor(null);
              props.setShowDeleteModal(true);
            }}
          >
            <ListItemIcon>
              <Delete fontSize="small" />
            </ListItemIcon>
            <ListItemText>Delete</ListItemText>
          </MenuItem>
        </Menu>
      </Paper>

      <Stack
        onClick={openDeck}
        sx={{
          display: { xs: 'none', sm: 'grid' },
          gridTemplateColumns: '50px minmax(140px, 1.5fr) 90px minmax(160px, 1fr) minmax(190px, 1fr) 56px',
          gap: listGap,
          width: '100%',
          '& > .MuiPaper-root': { minWidth: 0 },
        }}
      >
        <Paper sx={{ p: listPadding, alignContent: 'center', alignItems: 'center' }} elevation={4}>
          <DeckImage deck={deck} width={30} />
        </Paper>
        <Paper sx={{ p: listPadding, alignContent: 'center' }} elevation={4}>
          <Typography noWrap>{deck.name}</Typography>
        </Paper>
        <Paper sx={{ p: listPadding, alignContent: 'center' }} elevation={4}>
          <DeckAccessIcons {...props} />
        </Paper>
        <Paper sx={{ p: listPadding, alignContent: 'center' }} elevation={4}>
          <Typography variant="body2" color="text.secondary" noWrap>
            Created by {!isSetCreator ? deck.creatorName : 'You'}
          </Typography>
        </Paper>
        <Paper sx={{ p: listPadding, alignContent: 'center' }} elevation={4}>
          <Typography variant="caption">Last updated at {deck.modificationTimestamp || 'unknown'}</Typography>
        </Paper>

        <Paper
          onClick={(e) => {
            e.stopPropagation();
            if (canDelete) props.setShowDeleteModal(true);
          }}
          sx={{ alignContent: 'center', textAlign: 'center' }}
          elevation={4}
        >
          {canDelete && <Delete />}
        </Paper>
      </Stack>
    </Grid>
  );
};

const DeckAccessIcons = (props: CardExerciseProps) => (
  <Stack direction="row" spacing={1} alignItems="center">
    {props.isSetCreator && (
      <Tooltip title={`You are the creator of ${props.deck.name} and may modify the deck and its exercises and copy exercises from this deck to your other decks.`}>
        <WorkspacePremiumIcon fontSize="small" />
      </Tooltip>
    )}
    {props.isEditor && (
      <Tooltip title={`You are allowed to edit ${props.deck.name} and copy exercises from this deck to your decks.`}>
        <EditIcon fontSize="small" />
      </Tooltip>
    )}
    {!props.isSetCreator && !props.isEditor && (
      <Tooltip title={`You may view ${props.deck.name} and copy exercises from this deck to your decks.`}>
        <Visibility fontSize="small" />
      </Tooltip>
    )}
  </Stack>
);
